import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import {
  GENERATE_MY_QUOTE_SUGGESTIONS,
  MY_QUOTE_SUGGESTION_JOB,
  MY_QUOTE_SUGGESTION_QUOTA,
  type GenerateMyQuoteSuggestionsData,
  type GenerateMyQuoteSuggestionsVars,
  type MyQuoteSuggestionJobData,
  type MyQuoteSuggestionJobVars,
  type MyQuoteSuggestionQuotaData,
  type QuoteSuggestionQuoteDto,
  type QuoteSuggestionQuotaDto,
} from '@/queries/quoteCarousel';
import { mapQuoteSuggestionError, type QuoteSuggestionErrorKey } from '@/lib/quoteCarousel/mapQuoteSuggestionError';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120_000;

export type QuoteSuggestionState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'done'; quotes: QuoteSuggestionQuoteDto[] }
  | { status: 'error'; errorKey: QuoteSuggestionErrorKey };

export interface UseQuoteSuggestionsResult {
  state: QuoteSuggestionState;
  quota: QuoteSuggestionQuotaDto | null;
  generate: () => Promise<void>;
  reset: () => void;
}

/**
 * Drives `generateMyQuoteSuggestions` and polls `myQuoteSuggestionJob` every
 * `POLL_INTERVAL_MS` until it reaches a terminal `processing_status`
 * (`done`/`failed`) or `POLL_TIMEOUT_MS` elapses. Because the client-wide
 * Apollo default is `fetchPolicy: 'no-cache'` (see `src/lib/apollo-client.ts`),
 * both the quota read and the job poll go through
 * `client.query({ fetchPolicy: 'network-only' })` rather than a cache-driven
 * `useQuery` (mirrors `useAiCoverGeneration`).
 *
 * `myQuoteSuggestionQuota` is fetched once on mount (and again after every
 * terminal outcome) so `generate()` can refuse locally — with no mutation
 * call — once `quota.used >= quota.limit`.
 *
 * A poll that fails without a mapped error code (network blip, 5xx) is
 * retried on the next tick; only a mapped code (e.g. `not_found`) or the
 * deadline ends the flow. `generationRef` is a monotonically increasing
 * counter: starting a new `generate()` call bumps it, and every in-flight
 * timer/promise checks it before touching state, so a superseded
 * generation's late poll response is silently dropped instead of
 * clobbering the newer one.
 */
export function useQuoteSuggestions(clipId: string | null): UseQuoteSuggestionsResult {
  const client = useApolloClient();
  const [generateMutation] = useMutation<GenerateMyQuoteSuggestionsData, GenerateMyQuoteSuggestionsVars>(
    GENERATE_MY_QUOTE_SUGGESTIONS,
  );

  const [state, setState] = useState<QuoteSuggestionState>({ status: 'idle' });
  const [quota, setQuota] = useState<QuoteSuggestionQuotaDto | null>(null);

  const generationRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (deadlineTimerRef.current) {
      clearTimeout(deadlineTimerRef.current);
      deadlineTimerRef.current = null;
    }
  }, []);

  const refetchQuota = useCallback(async () => {
    try {
      const { data } = await client.query<MyQuoteSuggestionQuotaData>({
        query: MY_QUOTE_SUGGESTION_QUOTA,
        fetchPolicy: 'network-only',
      });
      if (data?.myQuoteSuggestionQuota) setQuota(data.myQuoteSuggestionQuota);
    } catch {
      // Best-effort: a stale/missing quota value is acceptable, the next
      // generation attempt (or completion) will refresh it.
    }
  }, [client]);

  useEffect(() => {
    void refetchQuota();
    // Fetch once on mount; quota is user-level (not clip-scoped), so it does
    // not need to re-fetch when `clipId` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    // Bump the generation so any in-flight poll promise that resolves after
    // unmount is recognized as stale and skips its setState.
    generationRef.current += 1;
    clearTimers();
  }, [clearTimers]);

  const reset = useCallback(() => {
    generationRef.current += 1;
    clearTimers();
    setState({ status: 'idle' });
  }, [clearTimers]);

  const schedulePoll = useCallback((id: string, generation: number) => {
    pollTimerRef.current = setTimeout(() => {
      pollTimerRef.current = null;
      if (generation !== generationRef.current) return;

      client
        .query<MyQuoteSuggestionJobData, MyQuoteSuggestionJobVars>({
          query: MY_QUOTE_SUGGESTION_JOB,
          variables: { id },
          fetchPolicy: 'network-only',
        })
        .then((response) => {
          if (generation !== generationRef.current) return;

          const job = response.data?.myQuoteSuggestionJob;
          if (!job) {
            schedulePoll(id, generation);
            return;
          }

          if (job.processing_status === 'done') {
            clearTimers();
            setState({ status: 'done', quotes: job.quotes ?? [] });
            void refetchQuota();
            return;
          }

          if (job.processing_status === 'failed') {
            clearTimers();
            setState({ status: 'error', errorKey: mapQuoteSuggestionError(job.error_code) });
            void refetchQuota();
            return;
          }

          schedulePoll(id, generation);
        })
        .catch((err: unknown) => {
          if (generation !== generationRef.current) return;
          const code = mapQuoteSuggestionError(err);
          if (code === 'generation_failed') {
            // Unmapped (network/5xx): the job keeps running server-side, so
            // keep polling until a terminal state or the deadline timer.
            schedulePoll(id, generation);
            return;
          }
          clearTimers();
          setState({ status: 'error', errorKey: code });
        });
    }, POLL_INTERVAL_MS);
  }, [client, clearTimers, refetchQuota]);

  const generate = useCallback(async () => {
    if (!clipId) return;

    if (quota && quota.used >= quota.limit) {
      setState({ status: 'error', errorKey: 'daily_limit_reached' });
      return;
    }

    clearTimers();
    generationRef.current += 1;
    const generation = generationRef.current;
    setState({ status: 'running' });

    try {
      const { data } = await generateMutation({ variables: { clipId } });
      if (generation !== generationRef.current) return;

      const job = data?.generateMyQuoteSuggestions;
      if (!job) {
        setState({ status: 'error', errorKey: 'generation_failed' });
        return;
      }

      if (job.processing_status === 'done') {
        clearTimers();
        setState({ status: 'done', quotes: job.quotes ?? [] });
        void refetchQuota();
        return;
      }

      if (job.processing_status === 'failed') {
        clearTimers();
        setState({ status: 'error', errorKey: mapQuoteSuggestionError(job.error_code) });
        void refetchQuota();
        return;
      }

      deadlineTimerRef.current = setTimeout(() => {
        if (generation !== generationRef.current) return;
        clearTimers();
        setState({ status: 'error', errorKey: 'timeout' });
      }, POLL_TIMEOUT_MS);

      schedulePoll(job.id, generation);
    } catch (err: unknown) {
      if (generation !== generationRef.current) return;
      setState({ status: 'error', errorKey: mapQuoteSuggestionError(err) });
    }
  }, [clearTimers, clipId, generateMutation, quota, refetchQuota, schedulePoll]);

  return { state, quota, generate, reset };
}
